import {Pill} from '@wandb/weave/components/Tag';
import React, {useEffect} from 'react';

import {useServiceHealthStore} from '../../../state/serviceHealth/store';
import {
  evaluateWindow,
  getWindowStats,
} from '../../../state/serviceHealth/tracker';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {NamedWorkspaceTooltip} from './NamedWorkspaceTooltip';

const TOOLTIP_CONTENT =
  'We are experiencing slower than usual performance.\n' +
  'Some actions may take longer to complete.';

const REEVALUATE_INTERVAL_MS = 10_000;

/**
 * Amber "Service degraded" pill shown while the backend is shedding GraphQL
 * queries (HTTP 503 or partial-shed 200+Retry-After over threshold — see
 * state/serviceHealth/tracker.ts). Not dismissible by design; it hides itself
 * when the unavailable rate recovers.
 */
export const ServiceDegradedPill = () => {
  const degraded = useServiceHealthStore(state => state.degraded);

  // The window normally re-evaluates as query samples arrive; with traffic
  // stopped (every panel already failed, user idle) nothing would prune it,
  // so poll while visible to let the pill auto-hide.
  useEffect(() => {
    if (!degraded) {
      return;
    }
    recordRumAction('service_degraded_alert', {
      status: 'shown',
      ...getWindowStats(),
    });
    const interval = setInterval(evaluateWindow, REEVALUATE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [degraded]);

  if (!degraded) {
    return null;
  }

  return (
    <NamedWorkspaceTooltip
      tooltipContent={TOOLTIP_CONTENT}
      tooltipWidth="26rem"
      dataTest="nw-service-degraded-pill"
      contentProps={{
        side: 'bottom',
        align: 'center',
      }}>
      <div className="whitespace-nowrap">
        <Pill
          color="sienna"
          icon="warning-alt"
          label="Service degraded"
          className="gap-4 rounded-md py-4 pl-8 pr-10 text-base [&_svg]:h-[18px] [&_svg]:w-[18px]"
        />
      </div>
    </NamedWorkspaceTooltip>
  );
};
